// test-options-view.ts — the developer "Test Options" form, brought back as a DOM
// overlay (the C++ DEV_BUILD TestOptionsForm, game/GameOptions.cpp:903-1020;
// [kidfTestOptions] in data/forms.ini.pp). A debug panel toggled by a keyboard
// shortcut (Alt+T; web/main.ts), available in ALL builds like the Alt+L / Alt+P
// overlays it sits alongside.
//
// SCOPE — only the toggles that have a REAL TS hook are ported (no fabricated hooks):
//   - Draw Paths       → dbgOverlay.paths (toggleDebugPaths; render/debugoverlay.ts).
//   - Draw Target Lines→ dbgOverlay.lines (toggleDebugLines).
//   - Show FPS         → dbgOverlay.showFps (toggleDebugShowFps) — SimUIForm reads
//                        `dbgOverlay.showFps || deps.fShowFPS`, so the DOM SimUIView
//                        FPS label (#dom-simui [data-simui="fps"]) appears live.
//   - God Mode         → the sim's ctx.fGodMode (sim/src/context.ts:109) — flipped on
//                        the running game's ctx (gamesession.ts: gs.sim.ctx).
//   - Clear Fog (button)→ ctx.fogm.RevealAll() (sim/src/sim/fogmap.ts:128 — the SAME
//                        hook SetObserving uses, gamesession.ts:2335).
// OMITTED (no TS port hook): Enable Overmind (the Overmind arm throws unported,
// sim/src/level.ts:5), Autosave (a per-tick host behaviour, not a flag), Max repaint,
// Suspend Updates, Lock Step, Draw Update Rects, Show stats, Memory…, Counts…, Break.
//
// The four checkboxes reflect + flip their LIVE flag (so the form stays in sync with
// the Alt+L / Alt+P / Alt+T shortcuts, which drive the SAME dbgOverlay flags), and
// Clear Fog calls the reveal hook. Each toggle sets dbgOverlay.dirty so the frame
// repaints. DISPLAY-only debug panel — no command / wire touch.
//
// Style mirrors the sibling in-game DOM forms (game-options-view / pause-menu-view):
// the shared chrome.ts design stage (formScale / makeStage / at / formTitle /
// makeStageButton) + the canvas RadioButton/CheckBox squareCheck look. The body is
// the SHIPPED [kidfTestOptions] geometry (FORM 0 0 160 151) — checkboxes in the left
// column (x=2, 9u apart), the action buttons (Force Reload / Clear Fog) + the OK close
// on the right — inset by MARGIN on every side so no control hugs the panel frame.

import type { ButtonArt } from '../title-art.ts';
import { zipInStage } from '../formchrome.ts';
import { ksfxGuiCheckBoxTap } from '../../ui/uikit.ts';
import {
  COL_FG,
  COL_ACCENT,
  TEXT_SHADOW,
  el,
  FONT_BODY,
  buttonWidthU,
  makeStage,
  at,
  formTitle,
  makeStageButton,
  type StageButtonSize,
} from '../chrome.ts';

/** A single live boolean toggle: its label + a live getter/flipper. */
export interface TestOption {
  label: string;
  get: () => boolean;
  toggle: () => void;
}

/** A momentary action button (e.g. Clear Fog) — only included when a real hook exists. */
export interface TestAction {
  label: string;
  run: () => void;
}

export interface TestOptionsViewDeps {
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** the live checkbox toggles (Draw Paths / Draw Target Lines / Show FPS / God Mode). */
  options: TestOption[];
  /** the momentary action buttons (Clear Fog) — empty when no hook is wired. */
  actions: TestAction[];
  /** Close (the OK / X) → the host unmounts the overlay. */
  onClose: () => void;
  /** the title-screen backdrop is NOT used (this floats over the live game); kept for
   *  signature parity with the sibling views — absent ⇒ a translucent dark panel. */
  buttonArt?: ButtonArt;
  /** play a GUI sfx (ksfxGui*) on a button/check press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

// an inner MARGIN (design units) insets every control from the panel frame so nothing
// hugs the edge. The centred title spans the full padded width, so it stays centred.
const MARGIN = 8;
// the SHIPPED [kidfTestOptions] body geometry (forms.ini.pp), design units — laid out
// inside the MARGIN inset (render() adds MARGIN to each placement). FORM is the body
// plus the margin on all four sides.
const BODY = { w: 160, h: 151 };
const FORM = { w: BODY.w + MARGIN * 2, h: BODY.h + MARGIN * 2 };
const TITLE = { x: 53, y: 2, w: 54 }; // "TEST OPTIONS" center kifntTitle
const CHECK_X = 2,
  CHECK_Y0 = 15,
  CHECK_DY = 9; // the left column of checkboxes
const ACTION = { x: 104, y0: 14, w: 54, h: 12, dy: 16 }; // the right column of buttons
const OK = { x: 51, y: 135, w: 54, h: 12 }; // OK (the close button)

export class TestOptionsView {
  private readonly d: TestOptionsViewDeps;
  /** the shared 9-slice button renderer (owns its own composite cache). */
  private readonly btn: (
    label: string,
    on: () => void,
    size?: StageButtonSize,
  ) => HTMLButtonElement;
  /** the ShellForm zip plays ONCE, on the first show — not on re-renders. */
  private animated = false;
  private formShowTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(d: TestOptionsViewDeps) {
    this.d = d;
    this.btn = makeStageButton(d); // fontFloor defaults to 8
    const s = d.root.style;
    // a fixed, full-window MODAL overlay at the in-game modal z-layer (20, like the
    // pause menu / game options). pointer-events:all so the checkbox clicks land here
    // (and the live canvas behind it gets nothing while the panel is up).
    s.position = 'fixed';
    s.inset = '0';
    s.display = 'flex';
    s.alignItems = 'center';
    s.justifyContent = 'center';
    s.zIndex = '20';
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden'; // clip controls parked off-window during the zip (no scrollbar flash)
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown);
    d.root.addEventListener('pointerdown', this.onBackdrop);
    this.render();
  }

  private readonly onResize = (): void => {
    this.render();
  };

  // Esc closes the debug panel (the C++ kidcOk default / kfFrmAutoTakedown).
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.d.onClose();
    }
  };

  // a tap on the dim backdrop (NOT the panel) closes it — a debug panel is throwaway.
  private readonly onBackdrop = (e: PointerEvent): void => {
    const stage = this.d.root.querySelector('[data-testopts-stage]');
    if (stage !== null && e.target instanceof Node && stage.contains(e.target)) return;
    this.d.onClose();
  };

  /** detach the listeners + cancel a pending form-show sfx (the host unmounts on close). */
  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
    this.d.root.removeEventListener('pointerdown', this.onBackdrop);
    if (this.formShowTimer !== null) {
      clearTimeout(this.formShowTimer);
      this.formShowTimer = null;
    }
  }

  private zipIn(stage: HTMLElement): void {
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
    this.formShowTimer = zipInStage(stage, this.d.playSfx);
  }

  private stageButton(
    s: number,
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    on: () => void,
  ): HTMLElement {
    return at(this.btn(label, on, { w: w * s, h: h * s }), s, x, y);
  }

  render(): void {
    const { stage, s } = makeStage(FORM.w, FORM.h);
    // the panel needs a visible body (the canvas form is FORMBACKCOLOR) — a translucent
    // dark fill + a thin accent frame over the live game, like the other dialog stages.
    Object.assign(stage.style, {
      background: 'rgba(7, 22, 27, 0.92)',
      border: `${Math.max(1, Math.round(s))}px solid ${COL_ACCENT}`,
      boxSizing: 'border-box',
    });
    stage.dataset.testoptsStage = '1';

    // title "TEST OPTIONS" (53,2,54) center kifntTitle — full-width centred.
    stage.append(at(formTitle('TEST OPTIONS', s), s, 0, TITLE.y + MARGIN, FORM.w));

    // the left column of LIVE checkboxes (Draw Paths / Draw Target Lines / Show FPS /
    // God Mode) — each reflects + flips its flag, then re-renders so the X follows.
    let cy = CHECK_Y0;
    for (const opt of this.d.options) {
      const e = this.squareCheck(
        opt.label,
        opt.get(),
        () => {
          opt.toggle();
          this.render(); // reflect the new state
        },
        s,
      );
      e.dataset.testopt = opt.label;
      stage.append(at(e, s, CHECK_X + MARGIN, cy + MARGIN, 96));
      cy += CHECK_DY;
    }

    // the right column of action buttons (Clear Fog) + … then the close OK below.
    let ay = ACTION.y0;
    for (const act of this.d.actions) {
      const b = this.stageButton(
        s,
        act.label,
        ACTION.x + MARGIN,
        ay + MARGIN,
        ACTION.w,
        ACTION.h,
        () => {
          act.run();
        },
      );
      (b as HTMLElement).dataset.testaction = act.label;
      stage.append(b);
      ay += ACTION.dy;
    }

    // OK (51,135,54,12) — the close button (the C++ form default). Sized to its label.
    const okW = buttonWidthU('OK', OK.h, s, 30);
    const ok = this.stageButton(s, 'OK', (FORM.w - okW) / 2, OK.y + MARGIN, okW, OK.h, () => {
      this.d.onClose();
    });
    (ok as HTMLElement).dataset.testoptsOk = '1';
    stage.append(ok);

    this.d.root.replaceChildren(stage);
    if (!this.animated) {
      this.animated = true;
      this.zipIn(stage);
    }
  }

  // the canvas RadioButton/CheckBox look: a square outlined box with an X when set, a
  // label to the right (shared with game-options-view's squareCheck, scaled to stage).
  private squareCheck(
    label: string,
    checked: boolean,
    onToggle: () => void,
    s: number,
  ): HTMLElement {
    const box = Math.round(7 * s);
    const row = el('label', {
      display: 'flex',
      alignItems: 'center',
      gap: `${Math.round(3 * s)}px`,
      fontFamily: FONT,
      fontSize: `${Math.round(5.5 * s)}px`,
      color: COL_FG,
      textShadow: shadow,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    });
    const b = el('span', {
      width: `${box}px`,
      height: `${box}px`,
      border: `${Math.max(1, Math.round(s))}px solid ${COL_ACCENT}`,
      position: 'relative',
      flex: '0 0 auto',
      boxSizing: 'border-box',
    });
    if (checked) {
      const x = COL_ACCENT;
      b.append(
        el('span', {
          position: 'absolute',
          inset: `${Math.max(1, Math.round(s * 0.5))}px`,
          background: `linear-gradient(45deg, transparent 41%, ${x} 41%, ${x} 59%, transparent 59%), linear-gradient(-45deg, transparent 41%, ${x} 41%, ${x} 59%, transparent 59%)`,
        }),
      );
    }
    const t = el('span', {});
    t.textContent = label;
    row.append(b, t);
    // preventDefault on pointerdown — the mousedown (not the click) starts a text
    // selection on a rapid toggle; the click still fires + toggles.
    row.addEventListener('pointerdown', (e) => {
      e.preventDefault();
    });
    row.addEventListener('click', (e) => {
      e.preventDefault();
      this.d.playSfx?.(ksfxGuiCheckBoxTap);
      onToggle();
    });
    return row;
  }
}
